import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Clock, AlertTriangle, AlertCircle, Info,
  Package, Music, Briefcase, FileText, ChevronRight,
  RefreshCw, Trash2, Loader2, Bell, ExternalLink
} from 'lucide-react';
import './ExpiringNotifications.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Item type icons
const ITEM_ICONS = {
  crate: Package,
  audio: Music,
  gig: Briefcase,
  promo: FileText,
  content: FileText
};

// Urgency colors and icons
const URGENCY_CONFIG = {
  critical: { icon: AlertTriangle, color: '#ef4444', label: 'Expires Today' },
  warning: { icon: AlertCircle, color: '#f59e0b', label: '1-3 Days' },
  notice: { icon: Clock, color: '#3b82f6', label: '3-7 Days' },
  info: { icon: Info, color: '#6b7280', label: '7+ Days' },
  expired: { icon: AlertTriangle, color: '#dc2626', label: 'Expired' }
};

const ExpiringNotifications = ({ isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extendingId, setExtendingId] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, crate, audio, gig, content

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  // Fetch expiring items
  const fetchExpiringItems = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = getAuthToken();
      if (!token) {
        setError('Please log in to view expiring items');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/expiring/all?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch expiring items');

      const data = await response.json();
      setItems(data.items || []);
      setSummary({
        total: data.total_expiring,
        critical: data.critical_count,
        warning: data.warning_count,
        notice: data.notice_count,
        categories: data.categories
      });
    } catch (err) {
      console.error('Error fetching expiring items:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    if (isOpen) {
      fetchExpiringItems();
    }
  }, [isOpen, fetchExpiringItems]);

  // Extend item expiration
  const extendItem = async (itemType, itemId) => {
    setExtendingId(itemId);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${BACKEND_URL}/api/expiring/${itemType}/${itemId}/extend?days=10`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to extend');

      // Refresh list
      await fetchExpiringItems();
    } catch (err) {
      console.error('Error extending item:', err);
      setError(err.message);
    } finally {
      setExtendingId(null);
    }
  };

  // Archive/dismiss item
  const archiveItem = async (itemType, itemId) => {
    setArchivingId(itemId);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${BACKEND_URL}/api/expiring/${itemType}/${itemId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to archive');

      // Refresh list
      await fetchExpiringItems();
    } catch (err) {
      console.error('Error archiving item:', err);
      setError(err.message);
    } finally {
      setArchivingId(null);
    }
  };

  // Navigate to item
  const navigateToItem = (item) => {
    if (item.action_url) {
      window.location.href = item.action_url;
      onClose();
    }
  };

  // Filter items
  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(item => item.item_type === filter);

  if (!isOpen) return null;

  return (
    <div className="expiring-modal-overlay" onClick={onClose}>
      <div className="expiring-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="expiring-modal-header">
          <div className="header-title">
            <Bell size={20} className="header-icon" />
            <h3>Expiring Items</h3>
            {summary && summary.total > 0 && (
              <span className="total-badge">{summary.total}</span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Summary Bar */}
        {summary && summary.total > 0 && (
          <div className="expiring-summary">
            {summary.critical > 0 && (
              <div className="summary-badge critical">
                <AlertTriangle size={14} />
                <span>{summary.critical} critical</span>
              </div>
            )}
            {summary.warning > 0 && (
              <div className="summary-badge warning">
                <AlertCircle size={14} />
                <span>{summary.warning} warning</span>
              </div>
            )}
            {summary.notice > 0 && (
              <div className="summary-badge notice">
                <Clock size={14} />
                <span>{summary.notice} soon</span>
              </div>
            )}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
            {summary?.total > 0 && <span className="count">{summary.total}</span>}
          </button>
          <button 
            className={`filter-tab ${filter === 'crate' ? 'active' : ''}`}
            onClick={() => setFilter('crate')}
          >
            <Package size={14} />
            Crates
            {summary?.categories?.crates > 0 && (
              <span className="count">{summary.categories.crates}</span>
            )}
          </button>
          <button 
            className={`filter-tab ${filter === 'audio' ? 'active' : ''}`}
            onClick={() => setFilter('audio')}
          >
            <Music size={14} />
            Audio
            {summary?.categories?.audio > 0 && (
              <span className="count">{summary.categories.audio}</span>
            )}
          </button>
          <button 
            className={`filter-tab ${filter === 'gig' ? 'active' : ''}`}
            onClick={() => setFilter('gig')}
          >
            <Briefcase size={14} />
            Gigs
            {summary?.categories?.gigs > 0 && (
              <span className="count">{summary.categories.gigs}</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="expiring-modal-content">
          {isLoading ? (
            <div className="loading-state">
              <Loader2 size={32} className="spinning" />
              <span>Loading expiring items...</span>
            </div>
          ) : error ? (
            <div className="error-state">
              <AlertTriangle size={32} />
              <span>{error}</span>
              <button onClick={fetchExpiringItems}>Try Again</button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state">
              <Clock size={48} />
              <h4>No Expiring Items</h4>
              <p>You&apos;re all caught up! No items are expiring soon.</p>
            </div>
          ) : (
            <div className="expiring-items-list">
              {filteredItems.map((item) => {
                const IconComponent = ITEM_ICONS[item.item_type] || FileText;
                const urgencyConfig = URGENCY_CONFIG[item.urgency] || URGENCY_CONFIG.info;
                const UrgencyIcon = urgencyConfig.icon;

                return (
                  <div 
                    key={`${item.item_type}-${item.id}`}
                    className={`expiring-item ${item.urgency}`}
                  >
                    <div className="item-icon-wrapper" style={{ borderColor: urgencyConfig.color }}>
                      <IconComponent size={20} />
                    </div>

                    <div className="item-content" onClick={() => navigateToItem(item)}>
                      <div className="item-header">
                        <span className="item-title">{item.title}</span>
                        <span 
                          className="urgency-badge" 
                          style={{ background: `${urgencyConfig.color}20`, color: urgencyConfig.color }}
                        >
                          <UrgencyIcon size={12} />
                          {item.hours_remaining < 24 
                            ? `${item.hours_remaining}h left`
                            : `${item.days_remaining}d left`
                          }
                        </span>
                      </div>
                      {item.description && (
                        <p className="item-description">{item.description}</p>
                      )}
                      <div className="item-meta">
                        <span className="item-type">{item.item_type}</span>
                        <ChevronRight size={14} className="nav-icon" />
                      </div>
                    </div>

                    <div className="item-actions">
                      {item.can_extend && (
                        <button 
                          className="extend-btn"
                          onClick={() => extendItem(item.item_type, item.id)}
                          disabled={extendingId === item.id}
                          title="Extend 10 days"
                        >
                          {extendingId === item.id ? (
                            <Loader2 size={16} className="spinning" />
                          ) : (
                            <RefreshCw size={16} />
                          )}
                        </button>
                      )}
                      <button 
                        className="archive-btn"
                        onClick={() => archiveItem(item.item_type, item.id)}
                        disabled={archivingId === item.id}
                        title="Archive"
                      >
                        {archivingId === item.id ? (
                          <Loader2 size={16} className="spinning" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Premium Upgrade */}
        <div className="expiring-modal-footer">
          <div className="premium-promo">
            <div className="promo-content">
              <h4>🚀 Premium Storage</h4>
              <p>Get unlimited storage with no expiration</p>
            </div>
            <button className="upgrade-btn" disabled>
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpiringNotifications;
